import React, { useState, useEffect, useRef, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import authFetch from '../api/authFetch';
import './Heatmap.css';

// ── Constants ──────────────────────────────────────────────────
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TOP_N_OPTIONS = [5, 10, 15, 20, 25, 50];

const COLLECTIONS = [
  { key: 'nifty50', label: 'N50',    cls: 'n50' },
  { key: 'fo',      label: 'F&O',    cls: 'fo'  },
  { key: 'cash',    label: 'CASH', cls: 'oth' },
];

// ── Helpers ────────────────────────────────────────────────────
const getApiBase = () => {
  if (process.env.REACT_APP_API_URL) return `${process.env.REACT_APP_API_URL}/market`;
  const h = window.location.hostname;
  return (h === 'localhost' || h === '127.0.0.1')
    ? '/api/market'
    : `http://${h}:5001/api/market`;
};

const parseDate  = (raw) => new Date(raw);
const getYear    = (d) => d.getUTCFullYear();
const getMonth   = (d) => d.getUTCMonth() + 1;
const getDay     = (d) => d.getUTCDate();

const formatDisplayDate = (isoStr) => {
  const d = parseDate(isoStr);
  return `${getDay(d)} ${MONTH_NAMES[getMonth(d) - 1]} ${getYear(d)}`;
};

const formatNumber = (n) => {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};

const fmtPct = (n) => {
  if (n == null || isNaN(n)) return '—';
  return (n >= 0 ? '+' : '') + Number(n).toFixed(2) + '%';
};

const fmtCsVal = (n) => {
  if (n == null || isNaN(n)) return '—';
  return (n >= 0 ? '+' : '') + formatNumber(n);
};

// ── Component ──────────────────────────────────────────────────
const Heatmap = () => {
  const API_BASE = getApiBase();

  // Filter state
  const [allDates, setAllDates]         = useState([]);
  const [years, setYears]               = useState([]);
  const [months, setMonths]             = useState([]);
  const [dates, setDates]               = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [collection, setCollection]     = useState('nifty50');
  const [topN, setTopN]                 = useState(10);

  // Data state
  const [stocks, setStocks]             = useState([]);
  const [bullishCount, setBullishCount] = useState(0);
  const [bearishCount, setBearishCount] = useState(0);

  // Loading states
  const [pageLoading, setPageLoading]   = useState(true);
  const [dataLoading, setDataLoading]   = useState(false);

  // Treemap container size (tracked via ResizeObserver)
  const [containerHeight, setContainerHeight] = useState(400);
  const treemapRef = useRef(null);

  // Toast
  const [toast, setToast] = useState({ show: false, msg: '', type: 'info' });
  const toastTimerRef = useRef(null);

  // Copy state
  const [copiedTable, setCopiedTable] = useState('');

  // Mobile/tablet filter bar visibility
  const [filterOpen, setFilterOpen] = useState(false);

  // Tooltip
  const [tooltip, setTooltip] = useState({
    visible: false, x: 0, y: 0,
    symbol: '', csValue: 0, pctChng: 0, iep: 0, qty: 0, type: 'bullish',
  });
  const tooltipRef = useRef(null);
  const tooltipAnchorRef = useRef(null);

  useEffect(() => {
    if (!tooltip.visible || !tooltipAnchorRef.current || !tooltipRef.current) return;

    const anchor = tooltipAnchorRef.current;
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const gap = 8;
    const edge = 8;
    const roomBelow = window.innerHeight - anchor.bottom;
    const showBelow = roomBelow >= tooltipRect.height + gap;
    const x = Math.max(
      edge,
      Math.min(anchor.left + (anchor.width - tooltipRect.width) / 2, window.innerWidth - tooltipRect.width - edge),
    );
    const y = showBelow
      ? anchor.bottom + gap
      : Math.max(edge, anchor.top - tooltipRect.height - gap);

    setTooltip((prev) => ({ ...prev, x, y }));
  }, [tooltip.visible, tooltip.symbol]);

  // ── Copy symbols for TradingView ────────────────────────────
  const copySymbols = (type) => {
    const filtered = stocks.filter((s) => s.type === type);
    if (!filtered.length) return;
    const text = filtered.map((s) => `NSE:${s.symbol}`).join(',');
    navigator.clipboard.writeText(text).catch(() => {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopiedTable(type);
    setTimeout(() => setCopiedTable(''), 2000);
  };

  // ── Toast helper ───────────────────────────────────────────
  const showToast = useCallback((msg, type = 'info') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ show: true, msg, type });
    toastTimerRef.current = setTimeout(
      () => setToast((t) => ({ ...t, show: false })),
      3500,
    );
  }, []);

  // ── Track treemap container height ────────────────────────
  useEffect(() => {
    const el = treemapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height || 400);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Initial load: fetch all dates ─────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const res = await authFetch(`${API_BASE}/dates`);
        if (!res.ok) throw new Error('Failed to fetch dates');
        const data = await res.json();

        const parsed = (data.allDates || [])
          .map((d) => (typeof d === 'string' ? d : new Date(d).toISOString()))
          .sort();

        if (parsed.length === 0) {
          showToast('No market data available', 'error');
          setPageLoading(false);
          return;
        }

        setAllDates(parsed);

        const yrSet = [...new Set(parsed.map((d) => getYear(parseDate(d))))].sort((a, b) => b - a);
        setYears(yrSet);
        setSelectedYear(yrSet[0]);
      } catch (err) {
        showToast('Error loading dates: ' + err.message, 'error');
      } finally {
        setPageLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Repopulate months when year changes ───────────────────
  useEffect(() => {
    if (!selectedYear || allDates.length === 0) return;
    const yearDates = allDates.filter((d) => getYear(parseDate(d)) === selectedYear);
    const moSet = [...new Set(yearDates.map((d) => getMonth(parseDate(d))))].sort((a, b) => b - a);
    setMonths(moSet);
    setSelectedMonth(moSet[0] || null);
  }, [selectedYear, allDates]);

  // ── Repopulate dates when month changes ───────────────────
  useEffect(() => {
    if (!selectedYear || !selectedMonth || allDates.length === 0) return;

    const filtered = allDates
      .map((d) => parseDate(d))
      .filter((d) => getYear(d) === selectedYear && getMonth(d) === selectedMonth);

    // Deduplicate by calendar day, sort newest first
    const seen = new Set();
    const uniqueDays = [];
    filtered
      .sort((a, b) => b - a)
      .forEach((d) => {
        const day = getDay(d);
        if (!seen.has(day)) { seen.add(day); uniqueDays.push(d); }
      });

    setDates(uniqueDays);
    setSelectedDate(uniqueDays.length > 0 ? uniqueDays[0].toISOString() : null);
  }, [selectedYear, selectedMonth, allDates]);

  // ── Fetch heatmap data when date / collection / topN changes
  useEffect(() => {
    if (!selectedDate) {
      setStocks([]);
      setBullishCount(0);
      setBearishCount(0);
      return;
    }
    fetchHeatmapData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, collection, topN]);

  const fetchHeatmapData = async () => {
    if (!selectedDate) return;
    setDataLoading(true);
    try {
      const url = `${API_BASE}/${collection}/heatmap?date=${encodeURIComponent(selectedDate)}&top=${topN}`;
      const res = await authFetch(url);
      if (!res.ok) throw new Error('Failed to fetch heatmap data');
      const data = await res.json();

      const bullish = (data.bullish || []).map((s) => ({ ...s, type: 'bullish' }));
      const bearish = (data.bearish || []).map((s) => ({ ...s, type: 'bearish' }));

      const combined = [...bullish, ...bearish].sort(
        (a, b) => Math.abs(b.pctChng || 0) - Math.abs(a.pctChng || 0),
      );

      setStocks(combined);
      setBullishCount(bullish.length);
      setBearishCount(bearish.length);
    } catch (err) {
      showToast('Error loading heatmap: ' + err.message, 'error');
    } finally {
      setDataLoading(false);
    }
  };

  // ── Treemap block geometry ─────────────────────────────────
  const buildBlocks = () => {
    if (!stocks.length) return null;

    const absValues = stocks.map((s) => Math.abs(s.pctChng || 0));
    const maxAbs    = Math.max(...absValues, 0.001);
    const totalAbs  = absValues.reduce((a, b) => a + b, 0) || 1;

    const total       = stocks.length;
    const perRow      = total <= 10 ? 5 : total <= 20 ? 6 : total <= 30 ? 8 : 10;
    const numRows     = Math.ceil(total / perRow);
    const gap         = 4;
    const avail       = containerHeight - (numRows - 1) * gap - 8;
    const blockHeight = Math.max(Math.floor(avail / numRows), 44);

    return stocks.map((stock, i) => {
      const absVal    = Math.abs(stock.pctChng || 0);
      const weight    = absVal / totalAbs;
      const intensity = Math.min(Math.ceil((absVal / maxAbs) * 5) || 1, 5);
      const flexBasis = Math.max(weight * 100, 7);

      return (
        <div
          key={`${stock.symbol}-${i}`}
          className={`hm-block ${stock.type} intensity-${intensity}`}
          style={{ flex: `1 1 ${flexBasis}%`, height: `${blockHeight}px` }}
          onMouseEnter={(e) => handleMouseEnter(e, stock)}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onPointerDown={(e) => handlePointerDown(e, stock)}
        >
          <span className="hm-block__symbol">{stock.symbol}</span>
          <span className="hm-block__pct">{fmtPct(stock.pctChng)}</span>
        </div>
      );
    });
  };

  // ── Tooltip events ─────────────────────────────────────────
  const handleMouseEnter = (e, stock) => {
    tooltipAnchorRef.current = null;
    const x = Math.min(e.clientX + 16, window.innerWidth  - 175);
    const y = Math.min(e.clientY + 16, window.innerHeight - 175);
    setTooltip({
      visible: true, x, y,
      symbol:  stock.symbol,
      csValue: stock.csValue,
      pctChng: stock.pctChng,
      iep:     stock.iep,
      qty:     stock.qty,
      type:    stock.type,
    });
  };

  const handleMouseMove = (e) => {
    const x = Math.min(e.clientX + 16, window.innerWidth  - 175);
    const y = Math.min(e.clientY + 16, window.innerHeight - 175);
    setTooltip((prev) => ({ ...prev, x, y }));
  };

  const handleMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  const handlePointerDown = (e, stock) => {
    if (e.pointerType === 'mouse') return;
    e.stopPropagation();

    if (tooltip.visible && tooltip.symbol === stock.symbol) {
      tooltipAnchorRef.current = null;
      setTooltip((prev) => ({ ...prev, visible: false }));
      return;
    }

    const anchor = e.currentTarget.getBoundingClientRect();
    tooltipAnchorRef.current = anchor;
    setTooltip({
      visible: true,
      x: anchor.left,
      y: anchor.bottom + 8,
      symbol: stock.symbol,
      csValue: stock.csValue,
      pctChng: stock.pctChng,
      iep: stock.iep,
      qty: stock.qty,
      type: stock.type,
    });
  };

  const handlePagePointerDown = (e) => {
    if (e.pointerType === 'mouse' || !tooltip.visible) return;
    tooltipAnchorRef.current = null;
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  // ═══════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <AppLayout pageBadge="Heat Map">
      {/* ── Page loading overlay ──────────────────────────── */}
      <div className={`hm-page-loading${pageLoading ? '' : ' hidden'}`}>
        <div className="hm-spinner" />
        <div className="hm-page-loading__text">Loading Heat Map…</div>
      </div>

      {/* ── Page root ─────────────────────────────────────── */}
      <div className="hm-page" onPointerDown={handlePagePointerDown}>

        {/* MOBILE/TABLET FILTER TOGGLE (shown only when bar is closed) */}
        {!filterOpen && (
          <button
            type="button"
            className="filter-toggle-btn"
            onClick={() => setFilterOpen(true)}
            aria-label="Show filters"
          >
            <span>☰</span>
            <span>Filters</span>
          </button>
        )}

        {/* ── Filter Bar ──────────────────────────────────── */}
        <div className={`hm-filterbar${filterOpen ? ' is-open' : ''}`}>

          {/* In-bar close button (mobile/tablet only) */}
          <button
            type="button"
            className="hm-filter-close"
            onClick={() => setFilterOpen(false)}
            aria-label="Close filters"
          >
            <span>✕</span>
            <span>Close Filter</span>
          </button>

          {/* Year */}
          <div className="hm-filter-group">
            <span className="hm-filter-label">Year</span>
            <select
              className="hm-select"
              value={selectedYear || ''}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {/* Month */}
          <div className="hm-filter-group">
            <span className="hm-filter-label">Month</span>
            <select
              className="hm-select"
              value={selectedMonth || ''}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
            >
              {months.map((m) => (
                <option key={m} value={m}>{MONTH_NAMES[m - 1]}</option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div className="hm-filter-group">
            <span className="hm-filter-label">Date</span>
            <input
              type="date"
              className="hm-select hm-date-input"
              value={selectedDate ? selectedDate.slice(0, 10) : ''}
              min={dates.length ? dates[dates.length - 1].toISOString().slice(0, 10) : ''}
              max={dates.length ? dates[0].toISOString().slice(0, 10) : ''}
              onChange={(e) => {
                const matchingDate = dates.find(
                  (date) => date.toISOString().slice(0, 10) === e.target.value,
                );

                if (matchingDate) {
                  setSelectedDate(matchingDate.toISOString());
                } else {
                  showToast('No market data available for this date', 'error');
                }
              }}
            />
          </div>

          <div className="hm-divider" />

          {/* Collection toggle */}
          <div className="hm-filter-group">
            <span className="hm-filter-label">Market</span>
            <div className="hm-toggle">
              {COLLECTIONS.map((c) => (
                <button
                  key={c.key}
                  className={`hm-toggle-btn ${c.cls}${collection === c.key ? ' active' : ''}`}
                  onClick={() => setCollection(c.key)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hm-divider" />

          {/* Top N toggle */}
          <div className="hm-filter-group">
            <span className="hm-filter-label">Top N</span>
            <div className="hm-toggle">
              {TOP_N_OPTIONS.map((n) => (
                <button
                  key={n}
                  className={`hm-toggle-btn topn${topN === n ? ' active' : ''}`}
                  onClick={() => setTopN(n)}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Selected date display */}
          <div className="hm-date-display">
            📅 <span>{selectedDate ? formatDisplayDate(selectedDate) : '—'}</span>
          </div>

          {/* Refresh */}
          <button
            className="hm-refresh-btn"
            onClick={fetchHeatmapData}
            disabled={!selectedDate || dataLoading}
            title="Refresh data"
          >
            ↻ Refresh
          </button>
        </div>

        {/* ── Main Content ─────────────────────────────────── */}
        <div className="hm-content">
          <div className="hm-card">

            {/* Card header */}
            <div className="hm-card-header">
              <div className="hm-card-title">🔥 Stock Heat Map</div>
              <div className="hm-badges">
                <span className="hm-badge bullish">
                  <span className="hm-badge-dot green" />
                  Bullish: {bullishCount}
                </span>
                <span className="hm-badge bearish">
                  <span className="hm-badge-dot red" />
                  Bearish: {bearishCount}
                </span>
                <button
                  onClick={() => copySymbols('bullish')}
                  disabled={!stocks.some((s) => s.type === 'bullish')}
                  title="Copy bullish symbols for TradingView"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: '0.68rem', fontWeight: 600,
                    padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                    border: copiedTable === 'bullish' ? '1px solid #10b981' : '1px solid rgba(16,185,129,0.4)',
                    background: copiedTable === 'bullish' ? 'rgba(16,185,129,0.25)' : 'rgba(16,185,129,0.1)',
                    color: copiedTable === 'bullish' ? '#6ee7b7' : '#10b981',
                    transition: 'all 0.2s',
                    opacity: stocks.some((s) => s.type === 'bullish') ? 1 : 0.4,
                  }}
                >
                  {copiedTable === 'bullish' ? '✓ Copied!' : '⧉ Bullish'}
                </button>
                <button
                  onClick={() => copySymbols('bearish')}
                  disabled={!stocks.some((s) => s.type === 'bearish')}
                  title="Copy bearish symbols for TradingView"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: '0.68rem', fontWeight: 600,
                    padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                    border: copiedTable === 'bearish' ? '1px solid #ef4444' : '1px solid rgba(239,68,68,0.4)',
                    background: copiedTable === 'bearish' ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.1)',
                    color: copiedTable === 'bearish' ? '#fca5a5' : '#ef4444',
                    transition: 'all 0.2s',
                    opacity: stocks.some((s) => s.type === 'bearish') ? 1 : 0.4,
                  }}
                >
                  {copiedTable === 'bearish' ? '✓ Copied!' : '⧉ Bearish'}
                </button>
              </div>
            </div>

            {/* Treemap body */}
            <div className="hm-card-body" ref={treemapRef}>
              {dataLoading ? (
                <div className="hm-loading">
                  <div className="hm-spinner" />
                  <span>Loading…</span>
                </div>
              ) : stocks.length === 0 ? (
                <div className="hm-empty">
                  <div className="hm-empty__icon">📊</div>
                  <div>Select a date to view the heat map</div>
                </div>
              ) : (
                buildBlocks()
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Tooltip ──────────────────────────────────────────── */}
      <div
        ref={tooltipRef}
        className={`hm-tooltip${tooltip.visible ? ' visible' : ' hidden'}`}
        style={{ left: tooltip.x, top: tooltip.y }}
        role="tooltip"
      >
        <div className={`hm-tooltip__symbol ${tooltip.type}`}>{tooltip.symbol}</div>
        <div className="hm-tooltip__row">
          <span className="hm-tooltip__label">CS Value</span>
          <span className={`hm-tooltip__val ${Number(tooltip.csValue) >= 0 ? 'hm-pos' : 'hm-neg'}`}>
            {fmtCsVal(tooltip.csValue)}
          </span>
        </div>
        <div className="hm-tooltip__row">
          <span className="hm-tooltip__label">% Change</span>
          <span className={`hm-tooltip__val ${Number(tooltip.pctChng) >= 0 ? 'hm-pos' : 'hm-neg'}`}>
            {fmtPct(tooltip.pctChng)}
          </span>
        </div>
        <div className="hm-tooltip__row">
          <span className="hm-tooltip__label">IEP</span>
          <span className={`hm-tooltip__val ${tooltip.type === 'bullish' ? 'hm-pos' : 'hm-neg'}`}>
            {formatNumber(tooltip.iep)}
          </span>
        </div>
        <div className="hm-tooltip__row">
          <span className="hm-tooltip__label">Qty</span>
          <span className={`hm-tooltip__val ${tooltip.type === 'bullish' ? 'hm-pos' : 'hm-neg'}`}>
            {tooltip.qty == null || isNaN(tooltip.qty) ? '—' : new Intl.NumberFormat('en-IN').format(tooltip.qty)}
          </span>
        </div>
      </div>

      {/* ── Toast ────────────────────────────────────────────── */}
      <div className={`hm-toast${toast.show ? ' show' : ''} ${toast.type}`}>
        {toast.msg}
      </div>
    </AppLayout>
  );
};

export default Heatmap;
